/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactElement, useState } from "react";

import { css, jsx } from "@compiled/react";

import { Field, HelperMessage } from "@atlaskit/form";
import Textfield from "@atlaskit/textfield";
import { G300, N500, R400 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import {
    SiteNameFieldTooltip,
    SiteNameSuffix,
    type ValidationStatus
} from "./components";

const fieldUpdateIsValidStyles = css({
    borderColor: token("color.text.success", G300)
});

const fieldLabelStyles = css({
    color: token("color.text.subtle", N500),
    fontFamily: token("font.family.brand.body"),
    fontStyle: "normal",
    fontWeight: token("font.weight.medium"),
    fontSize: "14px",
    lineHeight: "15px"
});

const fieldLabelWithTooltipStyles = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.050", "4px")
});

const errorMessageStyles = css({
    color: token("color.text.danger", R400),
    fontFamily: token("font.family.brand.body"),
    fontWeight: token("font.weight.regular"),
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "0",
    marginBlockStart: token("space.050", "4px")
});

export interface SiteNameFieldProps {
    readonly value: string;
    readonly fieldName: string;
    readonly fieldLabel?: string;
    readonly validate: ValidateSiteNameFieldValue;
    readonly renderPreview?: (value: string) => ReactElement;
}

// todo: how to import from atlaskit?
export type ValidateSiteNameFieldValue = (
    value: string | undefined
) => string | void | Promise<string | void>;

export const SiteNameField = ({
    value,
    fieldName,
    fieldLabel,
    renderPreview,
    validate
}: SiteNameFieldProps) => {
    const label = fieldLabel ? (
        <span css={fieldLabelWithTooltipStyles}>
            {fieldLabel} <SiteNameFieldTooltip />
        </span>
    ) : (
        ""
    );
    const [focused, setFocused] = useState(false);
    return (
        <Field
            label={<span css={fieldLabelStyles}>{label}</span>}
            name={fieldName}
            defaultValue={value}
            validate={validate}
        >
            {({ fieldProps, error, valid, meta }) => {
                const fieldUpdateIsValid = meta.dirty && valid;

                let validationStatus: ValidationStatus | undefined;
                if (meta.validating) {
                    validationStatus = "pending";
                } else if (error) {
                    validationStatus = "error";
                } else if (fieldUpdateIsValid) {
                    validationStatus = "valid";
                }

                const urlPreview = fieldProps.value
                    ? `${fieldProps.value}.atlassian.net`
                    : "your-company-name.atlassian.net";

                return (
                    <div>
                        <Textfield
                            elemAfterInput={
                                <SiteNameSuffix
                                    validationStatus={validationStatus}
                                    siteNameSuffixLabel={".atlassian.net"}
                                />
                            }
                            {...fieldProps}
                            onFocus={() => setFocused(true)}
                            onBlur={() => setFocused(false)}
                            css={[
                                fieldUpdateIsValid && fieldUpdateIsValidStyles
                            ]}
                        />
                        {error && <span css={errorMessageStyles}>{error}</span>}
                        {focused && !error && (
                            <HelperMessage>
                                Site name must be at least three characters
                                (numerals or lowercase letters only) and can't
                                start or end with a hyphen (-).
                            </HelperMessage>
                        )}
                        {renderPreview && renderPreview(urlPreview)}
                    </div>
                );
            }}
        </Field>
    );
};
